/**
 * BROWSER inventory agent (Vigía P-6).
 *
 * It is a web impl of the `InventoryCollector` pattern from `native-agent`: it collects
 * what the browser APIs expose (without opening up the machine) and **seals** the
 * part with SHA-256 of its canonical JSON (the same "pointer/seal" contract as in
 * Rust, but with JS-specific canonicalization — the seal is re-verified with the SAME
 * `sealInventory` function, it does not cross languages).
 *
 * Deep native collection (real CPU, disks, temperatures) is partial-manual
 * per platform (Tauri/CLI) — documented in `docs/DEVICE_AGENT_GUIDE.md`.
 */

import { isRecord } from "../p2p/guards";
import type { Inventory, SealedPart } from "./types";

const HEX = 16;
const HEX_PAD = 2;
const BYTES_PER_GIB = 1024 * 1024 * 1024;

/** Deterministic canonical serialization (keys sorted recursively). */
function canonicalize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(",")}]`;
  }
  if (!isRecord(value)) {
    return JSON.stringify(value);
  }
  const keys = Object.keys(value).toSorted();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalize(value[k])}`).join(",")}}`;
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(HEX).padStart(HEX_PAD, "0")).join("");
}

/** SHA-256 of the canonical inventory + version (the part's "seal"). */
export async function sealInventory(inventory: Inventory, version: number): Promise<string> {
  return sha256Hex(`v${version}:${canonicalize(inventory)}`);
}

/** Re-verifies that a part's seal matches its inventory. */
export async function verifyPart(part: SealedPart): Promise<boolean> {
  const expected = await sealInventory(part.inventory, part.version);
  return expected === part.seal;
}

interface NavigatorLike {
  hardwareConcurrency?: number;
  deviceMemory?: number;
  platform?: string;
  userAgent?: string;
  storage?: { estimate?: () => Promise<{ quota?: number; usage?: number }> };
}

/** OS detection by user-agent substring (no regex → lint-friendly). */
function detectOsName(ua: string, platform: string): string {
  const u = ua.toLowerCase();
  if (u.includes("windows")) {
    return "Windows";
  }
  if (u.includes("mac os x") || u.includes("macintosh")) {
    return "macOS";
  }
  if (u.includes("android")) {
    return "Android";
  }
  if (u.includes("iphone") || u.includes("ipad") || u.includes("ipod")) {
    return "iOS";
  }
  if (u.includes("linux")) {
    return "Linux";
  }
  return platform || "Unknown";
}

/**
 * Collects the current device's inventory with the browser APIs.
 * Guest-first: requires no account or special permissions.
 */
export async function collectBrowserInventory(deviceId: string): Promise<Inventory> {
  const nav: NavigatorLike = typeof navigator === "undefined" ? {} : navigator;
  const ua = nav.userAgent ?? "";
  const platform = nav.platform ?? "";
  const osName = detectOsName(ua, platform);
  const logical = nav.hardwareConcurrency ?? 0;
  const deviceMemGiB = nav.deviceMemory ?? 0;
  const totalBytes = deviceMemGiB > 0 ? deviceMemGiB * BYTES_PER_GIB : 0;

  let storageTotal = 0;
  let storageAvail = 0;
  try {
    const est = await nav.storage?.estimate?.();
    if (est) {
      storageTotal = est.quota ?? 0;
      storageAvail = Math.max(0, (est.quota ?? 0) - (est.usage ?? 0));
    }
  } catch {
    /* storage estimate unavailable: partial collection */
  }

  const hostname = typeof location === "undefined" ? "" : location.hostname;
  const online = typeof navigator === "undefined" || navigator.onLine;

  return {
    device_id: deviceId,
    collected_at: new Date().toISOString(),
    os: { name: osName, version: "", kernel: "", hostname, arch: platform },
    cpu: {
      model: "",
      arch: platform,
      physical_cores: 0,
      logical_cores: logical,
      max_freq_mhz: 0,
    },
    memory: { total_bytes: totalBytes, available_bytes: 0 },
    disks:
      storageTotal > 0
        ? [
            {
              name: "origin-storage",
              mount_point: "/",
              filesystem: "browser-quota",
              total_bytes: storageTotal,
              available_bytes: storageAvail,
            },
          ]
        : [],
    gpus: [],
    peripherals: [],
    packages: [],
    interfaces: [{ name: "web", mac: null, up: online }],
    temperatures: [],
  };
}

/** Collects + seals the inventory into a part ready to upload. */
export async function collectAndSeal(deviceId: string, version: number): Promise<SealedPart> {
  const inventory = await collectBrowserInventory(deviceId);
  const seal = await sealInventory(inventory, version);
  return { device_id: deviceId, version, collected_at: inventory.collected_at, inventory, seal };
}
